// ======================================================================
// FILE:
// src/modules/wms/template/components/TemplateItemChecklistsModal.js
//
// MODULE:
// Work Management System (WMS)
//
// FEATURE:
// Template Item Checklist Mapping
//
// PURPOSE:
// Manage Checklist Assignments
// for a Template Item
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import wmsChecklistService
from "../../services/wmsChecklistService";

import wmsTemplateChecklistService
from "../../services/wmsTemplateChecklistService";

const DEFAULT_FORM = {

  checklist_id: "",

  sequence_no: 1,

  mandatory: true,

  remarks: "",
};

export default function TemplateItemChecklistsModal({

  show,

  onClose,

  templateItemId,

  itemTitle,

}) {

  const [loading, setLoading] =
    useState(false);

  const [rows, setRows] =
    useState([]);

  const [checklists, setChecklists] =
    useState([]);

  const [showForm, setShowForm] =
    useState(false);

  const [editingRow, setEditingRow] =
    useState(null);

  const [form, setForm] =
    useState(DEFAULT_FORM);

  // ==========================================================
  // LOAD
  // ==========================================================

  useEffect(() => {

    if (
      show &&
      templateItemId
    ) {

      loadMappings();

      loadChecklists();
    }

  }, [
    show,
    templateItemId,
  ]);

  async function loadMappings() {

    try {

      setLoading(true);

      const data =
        await wmsTemplateChecklistService
          .getTemplateItemChecklists(
            templateItemId
          );

      setRows(data || []);

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Failed to load mappings"
      );

    } finally {

      setLoading(false);
    }
  }

  async function loadChecklists() {

    try {

      const result =
        await wmsChecklistService
          .listChecklists();

      setChecklists(
        result.items || []
      );

    } catch (err) {

      console.error(err);
    }
  }

  // ==========================================================
  // ADD
  // ==========================================================

  function handleAdd() {

    setEditingRow(null);

    setForm(
      DEFAULT_FORM
    );

    setShowForm(true);
  }

  // ==========================================================
  // EDIT
  // ==========================================================

  function handleEdit(row) {

    setEditingRow(row);

    setForm({

      checklist_id:
        row.checklist_id,

      sequence_no:
        row.sequence_no,

      mandatory:
        row.mandatory,

      remarks:
        row.remarks || "",
    });

    setShowForm(true);
  }

  // ==========================================================
  // SAVE
  // ==========================================================

  async function handleSave() {

    if (!form.checklist_id) {

      alert(
        "Checklist is required"
      );

      return;
    }

    try {

      if (editingRow) {

        await
        wmsTemplateChecklistService
          .updateTemplateItemChecklist(
            templateItemId,
            editingRow.id,
            form
          );

      } else {

        await
        wmsTemplateChecklistService
          .createTemplateItemChecklist(
            templateItemId,
            form
          );
      }

      setShowForm(false);

      await loadMappings();

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Save failed"
      );
    }
  }

  // ==========================================================
  // DELETE
  // ==========================================================

  async function handleDelete(row) {

    const ok =
      window.confirm(
        `Delete ${row.checklist_name}?`
      );

    if (!ok) {
      return;
    }

    try {

      await
      wmsTemplateChecklistService
        .deleteTemplateItemChecklist(
          templateItemId,
          row.id
        );

      await loadMappings();

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Delete failed"
      );
    }
  }

  if (!show) {
    return null;
  }

  return (

    <div
      className="modal d-block"
      tabIndex="-1"
    >

      <div className="modal-dialog modal-xl">

        <div className="modal-content">

          <div className="modal-header">

            <div>

              <h5 className="mb-1">
                Manage Item Checklists
              </h5>

              <small>
                {itemTitle}
              </small>

            </div>

            <button
              className="btn-close"
              onClick={onClose}
            />

          </div>

          <div className="modal-body">

            {!showForm && (

              <>
                <div
                  className="
                    d-flex
                    justify-content-between
                    mb-3
                  "
                >

                  <button
                    className="
                      btn btn-primary
                    "
                    onClick={handleAdd}
                  >
                    Add Checklist
                  </button>

                </div>

                <table
                  className="
                    table
                    table-bordered
                    table-striped
                  "
                >

                  <thead>

                    <tr>

                      <th>
                        Seq
                      </th>

                      <th>
                        Checklist Code
                      </th>

                      <th>
                        Checklist Name
                      </th>

                      <th>
                        Mandatory
                      </th>

                      <th>
                        Actions
                      </th>

                    </tr>

                  </thead>

                  <tbody>

                    {loading && (

                      <tr>

                        <td
                          colSpan="5"
                          className="
                            text-center
                          "
                        >
                          Loading...
                        </td>

                      </tr>
                    )}

                    {!loading &&
                      rows.map(
                        (row) => (

                        <tr
                          key={row.id}
                        >

                          <td>
                            {row.sequence_no}
                          </td>

                          <td>
                            {row.checklist_code}
                          </td>

                          <td>
                            {row.checklist_name}
                          </td>

                          <td>

                            {row.mandatory
                              ? "Yes"
                              : "No"}

                          </td>

                          <td>

                            <button
                              className="
                                btn
                                btn-sm
                                btn-primary
                                me-1
                              "
                              onClick={() =>
                                handleEdit(
                                  row
                                )
                              }
                            >
                              Edit
                            </button>

                            <button
                              className="
                                btn
                                btn-sm
                                btn-danger
                              "
                              onClick={() =>
                                handleDelete(
                                  row
                                )
                              }
                            >
                              Delete
                            </button>

                          </td>

                        </tr>

                    ))}

                  </tbody>

                </table>

              </>
            )}

            {showForm && (

              <div className="row">

                <div className="col-md-12 mb-3">

                  <label>
                    Checklist *
                  </label>

                  <select
                    className="
                      form-control
                    "
                    value={
                      form.checklist_id
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        checklist_id:
                          e.target.value,
                      })
                    }
                  >

                    <option value="">
                      Select Checklist
                    </option>

                    {checklists.map(
                      (c) => (

                      <option
                        key={c.id}
                        value={c.id}
                      >
                        {c.checklist_code}
                        {" - "}
                        {c.checklist_name}
                      </option>

                    ))}
                  </select>

                </div>

                <div className="col-md-6 mb-3">

                  <label>
                    Sequence No
                  </label>

                  <input
                    type="number"
                    className="
                      form-control
                    "
                    value={
                      form.sequence_no
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        sequence_no:
                          e.target.value,
                      })
                    }
                  />

                </div>

                <div className="col-md-6 mb-3">

                  <div
                    className="
                      form-check
                      mt-4
                    "
                  >

                    <input
                      type="checkbox"
                      className="
                        form-check-input
                      "
                      checked={
                        form.mandatory
                      }
                      onChange={(e) =>
                        setForm({
                          ...form,
                          mandatory:
                            e.target.checked,
                        })
                      }
                    />

                    <label
                      className="
                        form-check-label
                      "
                    >
                      Mandatory
                    </label>

                  </div>

                </div>

                <div className="col-md-12">

                  <label>
                    Remarks
                  </label>

                  <textarea
                    rows="3"
                    className="
                      form-control
                    "
                    value={
                      form.remarks
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        remarks:
                          e.target.value,
                      })
                    }
                  />

                </div>

              </div>

            )}

          </div>

          <div className="modal-footer">

            {showForm && (

              <button
                className="
                  btn btn-primary
                "
                onClick={handleSave}
              >
                Save
              </button>

            )}

            {showForm && (

              <button
                className="
                  btn btn-secondary
                "
                onClick={() =>
                  setShowForm(false)
                }
              >
                Back
              </button>

            )}

            {!showForm && (

              <button
                className="
                  btn btn-secondary
                "
                onClick={onClose}
              >
                Close
              </button>

            )}

          </div>

        </div>

      </div>

    </div>
  );
}